import { useState } from "react";
import { useCreate, useList, useGo } from "@refinedev/core";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FormField,
  FormHeader,
  FormStickyActions,
  FormSpacer,
  PermissionsSelector,
  FormCard,
  type PermissionOption,
} from "@/components/forms";
import { clearPermissionsCache } from "@/providers/access-control-provider";
import type { RolePayload } from "./types";

/**
 * Role create page
 */
export function RolesCreate() {
  const go = useGo();
  const [formData, setFormData] = useState<RolePayload>({
    displayName: "",
    description: "",
    permissions: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load permissions
  const { result: permissionsResult, query: permissionsQuery } =
    useList<PermissionOption>({
      resource: "permissions",
      pagination: { currentPage: 1, pageSize: 200, mode: "server" },
    });

  const permissions = permissionsResult.data ?? [];
  const permissionsLoading = permissionsQuery.isLoading;

  // Create mutation
  const { mutate: createMutate, mutation: createMutationState } = useCreate();

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = "Название обязательно";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {return;}

    createMutate(
      {
        resource: "roles",
        values: formData,
      },
      {
        onSuccess: () => {
          // Очищаем кэш прав чтобы новая роль была доступна
          clearPermissionsCache();
          go({ to: "/roles" });
        },
      }
    );
  };

  const togglePermission = (permissionCode: string) => {
    setFormData((prev) => {
      const codes = prev.permissions ?? [];
      if (codes.includes(permissionCode)) {
        return {
          ...prev,
          permissions: codes.filter((code) => code !== permissionCode),
        };
      } else {
        return { ...prev, permissions: [...codes, permissionCode] };
      }
    });
  };

  const toggleCategory = (categoryCodes: string[]) => {
    const allSelected = categoryCodes.every((code) =>
      formData.permissions?.includes(code)
    );

    setFormData((prev) => {
      if (allSelected) {
        return {
          ...prev,
          permissions: (prev.permissions ?? []).filter(
            (code) => !categoryCodes.includes(code)
          ),
        };
      } else {
        const existing = new Set(prev.permissions ?? []);
        categoryCodes.forEach((code) => existing.add(code));
        return { ...prev, permissions: [...existing] };
      }
    });
  };

  const isPending = createMutationState.isPending;

  return (
    <div className="space-y-6">
      <FormHeader
        title="Создать роль"
        description="Добавление новой роли в систему"
        onBack={() => go({ to: "/roles" })}
        sticky
      />

      <form onSubmit={handleSubmit} className="relative">
        <div className="grid gap-6 max-w-4xl pb-24">
          {/* Basic info */}
          <FormCard
            title="Основная информация"
            description="Название и описание роли"
          >
            <div className="space-y-4">
              <FormField
                label="Название"
                name="displayName"
                required
                error={errors.displayName}
              >
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                  placeholder="Менеджер"
                  disabled={isPending}
                />
              </FormField>

              <FormField label="Описание" name="description">
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Опишите назначение роли..."
                  disabled={isPending}
                  rows={3}
                />
              </FormField>
            </div>
          </FormCard>

          {/* Permissions */}
          <FormCard
            title="Права доступа"
            description="Выберите права для этой роли"
          >
            {permissionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <PermissionsSelector
                permissions={permissions}
                selectedCodes={formData.permissions ?? []}
                onTogglePermission={togglePermission}
                onToggleCategory={toggleCategory}
                isDisabled={isPending}
              />
            )}
          </FormCard>
        </div>

        <FormSpacer />

        <FormStickyActions
          primaryLabel="Создать роль"
          secondaryLabel="Отмена"
          onSecondary={() => go({ to: "/roles" })}
          isPending={isPending}
          pendingLabel="Создание..."
          secondaryVariant="secondary"
        />
      </form>
    </div>
  );
}
